import { useEffect, useState, useCallback } from "react";
import { Megaphone, Loader2, Plus, Pencil, Archive, X, MapPin, Calendar, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";

const STATUS_COLOR = {
  published: { color: GREEN, bg: "rgba(30,126,69,.10)" },
  scheduled: { color: "#B7791F", bg: "rgba(216,154,0,.14)" },
  draft: { color: SLATE, bg: "rgba(119,127,136,.12)" },
  archived: { color: "#9CA3AF", bg: "rgba(156,163,175,.12)" },
};
const TAB_KEYS = [["", "tab_all"], ["published", "tab_published"], ["scheduled", "tab_scheduled"], ["draft", "tab_draft"], ["archived", "tab_archived"]];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const emptyForm = { type: "news", title: "", body: "", status: "draft", location_id: "" };

export default function AktionenNews() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("");
  const [myRank, setMyRank] = useState(0);
  const [locs, setLocs] = useState([]);
  const [multi, setMulti] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const canManage = myRank >= 3;
  const locName = (id) => (id ? (locs.find((l) => l.id === id)?.name || t("common.location")) : t("common.all_locations"));

  const load = useCallback((status) => {
    setLoading(true);
    const q = status ? `?status=${status}` : "";
    api.get(`/news${q}`).then(({ data }) => setItems(data.news || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(tab); }, [load, tab]);
  useEffect(() => {
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setMyRank({ owner: 4, admin: 3, editor: 2, employee: 1 }[data.tenant.role] || 0); }).catch(() => {});
    api.get("/me/locations").then(({ data }) => { setLocs(data.locations || []); setMulti(!!data.multi); }).catch(() => {});
  }, []);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (n) => { setForm({ type: n.type || "news", title: n.title || "", body: n.body || "", status: n.status || "draft", location_id: n.location_id || "" }); setEditing(n.id); };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t("news.err_title")); return; }
    setSaving(true);
    const payload = { type: form.type, title: form.title.trim(), body: form.body, status: form.status };
    if (multi) payload.location_id = form.location_id || null;
    try {
      if (editing === "new") { await api.post("/news", payload); toast.success(t("news.toast_created")); }
      else { await api.put(`/news/${editing}`, payload); toast.success(t("news.toast_updated")); }
      close(); load(tab);
    } catch (e2) { toast.error(e2.response?.data?.detail || t("news.err_save")); }
    finally { setSaving(false); }
  };

  const publish = async (n) => {
    try { await api.put(`/news/${n.id}`, { status: "published" }); toast.success(t("news.toast_published")); load(tab); }
    catch (e) { toast.error(e.response?.data?.detail || t("news.err")); }
  };
  const archive = async (n) => {
    if (!window.confirm(t("news.archive_confirm", { title: n.title }))) return;
    try { await api.delete(`/news/${n.id}`); toast.success(t("news.toast_archived")); load(tab); }
    catch (e) { toast.error(e.response?.data?.detail || t("news.err")); }
  };

  return (
    <CockpitLayout active="aktionen" title={t("news.title")}>
      <div className="fundio-page-stack" data-testid="gold-aktionen">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" style={{ color: INK }} data-testid="news-title">
              <Megaphone size={28} style={{ color: GOLD }} /> {t("news.title")}
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">{t("news.subtitle")}</p>
          </div>
          {canManage && (
            <button data-testid="news-new-btn" onClick={openNew} className="fundio-action fundio-action--gold">
              <Plus size={16} /> {t("news.create")}
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2" data-testid="news-tabs">
          {TAB_KEYS.map(([val, key]) => (
            <button key={val || "all"} data-testid={`news-tab-${val || "all"}`} onClick={() => setTab(val)}
              className="rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors"
              style={tab === val ? { background: GOLD, color: INK } : { border: `1px solid ${LINE}`, color: SLATE, background: "#fff" }}>{t(`news.${key}`)}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : items.length === 0 ? (
          <div className="fundio-panel p-10 text-center" style={{ borderStyle: "dashed" }} data-testid="news-empty">
            <Megaphone size={30} className="mx-auto" style={{ color: LINE }} />
            <p className="mt-3 text-[15px] font-semibold" style={{ color: INK }}>{t("news.empty_title")}</p>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>{t("news.empty_body")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="news-list">
            {items.map((n) => {
              const st = STATUS_COLOR[n.status] || STATUS_COLOR.draft;
              return (
                <div key={n.id} className="fundio-panel" data-testid={`news-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: n.type === "action" ? "rgba(216,154,0,.14)" : "rgba(119,127,136,.12)", color: n.type === "action" ? GOLD : SLATE }}>{n.type === "action" ? t("news.type_action") : t("news.type_news")}</span>
                        <h3 className="truncate text-[16px] font-bold" style={{ color: INK }}>{n.title}</h3>
                      </div>
                      {n.body && <p className="mt-1.5 text-[13.5px] leading-relaxed" style={{ color: MUTED }}>{n.body}</p>}
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]" style={{ color: SLATE }}>
                        {n.published_at && <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(n.published_at)}</span>}
                        {n.start_at && <span className="flex items-center gap-1"><Calendar size={12} /> {t("news.from", { date: fmtDate(n.start_at) })}</span>}
                        {multi && <span className="flex items-center gap-1"><MapPin size={12} /> {locName(n.location_id)}</span>}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase" style={{ background: st.bg, color: st.color }} data-testid={`news-status-${n.id}`}>{t(`news.status_${n.status}`)}</span>
                  </div>
                  {canManage && (
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3" style={{ borderColor: LINE }}>
                      <button data-testid={`news-edit-${n.id}`} onClick={() => openEdit(n)} className="fundio-action" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13 }}><Pencil size={14} /> {t("news.edit")}</button>
                      {n.status !== "published" && n.status !== "archived" && (
                        <button data-testid={`news-publish-${n.id}`} onClick={() => publish(n)} className="fundio-action fundio-action--gold" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13 }}><Send size={14} /> {t("news.publish")}</button>
                      )}
                      {n.status !== "archived" && (
                        <button data-testid={`news-archive-${n.id}`} onClick={() => archive(n)} className="fundio-action" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13, color: "#903824", borderColor: "#E7B9AF" }}><Archive size={14} /> {t("news.archive")}</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" data-testid="news-editor">
          <div className="fundio-panel w-full max-w-lg rounded-t-2xl sm:rounded-2xl" style={{ maxHeight: "92vh", overflowY: "auto" }}>
            <div className="flex items-center justify-between">
              <h2 className="text-[18px] font-bold" style={{ color: INK }}>{editing === "new" ? t("news.form_create") : t("news.form_edit")}</h2>
              <button onClick={close} data-testid="news-editor-close" className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-fundio-subtle" style={{ color: SLATE }}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="mt-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("news.type")}</label>
                  <select data-testid="news-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="fundio-input">
                    <option value="news">{t("news.type_news")}</option><option value="action">{t("news.type_action")}</option>
                  </select></div>
                <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("news.status")}</label>
                  <select data-testid="news-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fundio-input">
                    <option value="draft">{t("news.status_draft")}</option><option value="scheduled">{t("news.status_scheduled")}</option><option value="published">{t("news.status_published")}</option>
                  </select></div>
              </div>
              <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("news.title_label")}</label>
                <input data-testid="news-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="fundio-input" placeholder={t("news.title_ph")} /></div>
              <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("news.body_label")}</label>
                <textarea data-testid="news-form-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="fundio-input" placeholder={t("news.body_ph")} /></div>
              {multi && (
                <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("common.location")}</label>
                  <select data-testid="news-form-location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="fundio-input">
                    <option value="">{t("common.all_locations")}</option>
                    {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select></div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={close} className="fundio-action">{t("common.cancel")}</button>
                <button type="submit" data-testid="news-form-submit" disabled={saving} className="fundio-action fundio-action--gold">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null} {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CockpitLayout>
  );
}
